/**
 * Provider config validation schemas.
 * Defines product providers and their branding configuration.
 */
import { z } from "zod";
// ============================================================================
// Common Patterns
// ============================================================================
/** Hex color pattern */
const hexColorPattern = /^#[0-9A-Fa-f]{6}$/;
// ============================================================================
// Provider Status
// ============================================================================
/** Provider status */
export const ProviderStatusSchema = z.enum(["active", "inactive", "suspended"]);
// ============================================================================
// Provider Branding
// ============================================================================
/** Provider branding configuration */
export const ProviderBrandingSchema = z
    .object({
    logoUrl: z.string().url().optional(),
    logoAltText: z.string().min(1).max(100).optional(),
    iconUrl: z.string().url().optional(),
    primaryColor: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    secondaryColor: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    backgroundColor: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    tagline: z.string().max(200).optional(),
})
    .strict();
// ============================================================================
// Provider Contact Information
// ============================================================================
/** Provider contact information */
export const ProviderContactSchema = z
    .object({
    email: z.string().email().optional(),
    phone: z.string().min(1).max(30).optional(),
    website: z.string().url().optional(),
    supportUrl: z.string().url().optional(),
    address: z
        .object({
        street: z.string().max(200).optional(),
        city: z.string().max(100).optional(),
        state: z.string().max(100).optional(),
        postalCode: z.string().max(20).optional(),
        country: z.string().max(100).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ============================================================================
// Provider Affiliate Configuration
// ============================================================================
/** Provider affiliate configuration */
export const ProviderAffiliateConfigSchema = z
    .object({
    enabled: z.boolean(),
    affiliateId: z.string().min(1).max(100).optional(),
    trackingUrl: z.string().url().optional(),
    trackingParams: z.record(z.string()).optional(),
    commissionType: z.enum(["cpa", "cpc", "cpl", "revenue_share"]).optional(),
    commissionValue: z.number().min(0).optional(),
})
    .strict();
// ============================================================================
// Provider Configuration
// ============================================================================
/** Complete provider configuration */
export const ProviderConfigSchema = z
    .object({
    id: z.string().min(1),
    externalId: z.string().min(1).optional(),
    name: z.string().min(1).max(200),
    slug: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z][a-z0-9-]*$/, "Slug must be kebab-case"),
    displayName: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    status: ProviderStatusSchema,
    branding: ProviderBrandingSchema.optional(),
    contact: ProviderContactSchema.optional(),
    affiliate: ProviderAffiliateConfigSchema.optional(),
    verticals: z.array(z.string()).optional(),
    metadata: z.record(z.unknown()).optional(),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
})
    .strict();
// ============================================================================
// Provider List Item
// ============================================================================
/** Provider list item for display */
export const ProviderListItemSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    displayName: z.string().optional(),
    logoUrl: z.string().optional(),
    status: ProviderStatusSchema,
    productCount: z.number().int().min(0).optional(),
    verticals: z.array(z.string()).optional(),
})
    .strict();
// ============================================================================
// Provider Creation/Update Inputs
// ============================================================================
/** Input for creating a new provider */
export const CreateProviderInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    slug: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z][a-z0-9-]*$/, "Slug must be kebab-case"),
    displayName: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    branding: ProviderBrandingSchema.optional(),
    contact: ProviderContactSchema.optional(),
    affiliate: ProviderAffiliateConfigSchema.optional(),
    verticals: z.array(z.string()).optional(),
})
    .strict();
/** Input for updating provider */
export const UpdateProviderInputSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    displayName: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    status: ProviderStatusSchema.optional(),
    branding: ProviderBrandingSchema.optional(),
    contact: ProviderContactSchema.optional(),
    affiliate: ProviderAffiliateConfigSchema.optional(),
    verticals: z.array(z.string()).optional(),
})
    .strict();
